import { useEffect } from 'react';
import { LearnerTrainingStatus, RecordReviewTimeDocument } from '~/data';
import { client } from '~/data/client';
import { useTrainingStore } from '../state/useTrainingStore';

export const REVIEW_HEARTBEAT_INTERVAL = 10_000;
export const REVIEW_IDLE_TIMEOUT = 120_000;

export function useCourseReviewTimer() {
  const learnerTrainingId = useTrainingStore(s => s.learnerTrainingId);
  const enabled = useTrainingStore(
    s =>
      !!s.shouldDisplayTimeRemainingPill &&
      s.status === LearnerTrainingStatus.InProgress &&
      s.remainingDurationInSecondsForCourseTimers !== 0
  );

  useEffect(() => {
    if (!enabled || !learnerTrainingId) return;

    let cancelled = false;
    let pending = false;
    let queued = false;
    let queuedStop = false;
    let firstHeartbeat = true;
    let lastActivity = Date.now();
    let intersecting = true;
    let lastSentActive: boolean | undefined;

    const isActive = () => {
      const mediaPlaying = Array.from(
        document.querySelectorAll('audio, video')
      ).some(
        media =>
          !(media as HTMLMediaElement).paused &&
          !(media as HTMLMediaElement).ended
      );
      return (
        document.visibilityState === 'visible' &&
        intersecting &&
        (Date.now() - lastActivity < REVIEW_IDLE_TIMEOUT || mediaPlaying)
      );
    };

    const tick = async () => {
      if (cancelled) return;
      const currentlyActive = isActive();
      useTrainingStore.setState({ reviewTimerPaused: !currentlyActive });
      if (pending) {
        queued = true;
        queuedStop ||= !currentlyActive;
        return;
      }
      const active = queuedStop ? false : currentlyActive;
      queuedStop = false;
      // A hidden/idle frame sends one stop, then stays quiet until resumed.
      if (!active && lastSentActive === false) {
        if (currentlyActive) void tick();
        return;
      }

      pending = true;
      queued = !active && currentlyActive;
      const reset = firstHeartbeat;
      try {
        const result = await client.mutate({
          mutation: RecordReviewTimeDocument,
          variables: { learnerTrainingUuid: learnerTrainingId, active, reset }
        });
        if (
          cancelled ||
          useTrainingStore.getState().learnerTrainingId !== learnerTrainingId
        )
          return;
        const timer = result.data?.recordReviewTime;
        if (timer?.errors?.length || !timer?.learnerTraining)
          throw Error('Review timer unavailable');
        if (timer.learnerTraining.id !== learnerTrainingId) return;
        firstHeartbeat = false;
        lastSentActive = active;
        useTrainingStore.setState({
          remainingDurationInMinutesForCourseTimers:
            timer.learnerTraining.remainingDurationInMinutesForCourseTimers,
          remainingDurationInSecondsForCourseTimers:
            timer.learnerTraining.remainingDurationInSecondsForCourseTimers,
          reviewTimerPaused: !isActive()
        });
      } catch {
        // A missed stop or uncertain response cannot carry active credit
        // across the next observation, even when the network recovers fast.
        firstHeartbeat = true;
        if (!cancelled) useTrainingStore.setState({ reviewTimerPaused: true });
      } finally {
        pending = false;
        if ((queued || queuedStop) && !cancelled) void tick();
      }
    };

    const activity = () => {
      const wasActive = isActive();
      lastActivity = Date.now();
      if (!wasActive) void tick();
    };
    const visibilityChanged = () => {
      if (document.visibilityState === 'visible') lastActivity = Date.now();
      void tick();
    };
    const events = [
      'pointerdown',
      'pointermove',
      'keydown',
      'touchstart',
      'wheel'
    ];
    events.forEach(event =>
      document.addEventListener(event, activity, { passive: true })
    );
    document.addEventListener('visibilitychange', visibilityChanged);

    const root = document.getElementById('root');
    const observer =
      typeof IntersectionObserver !== 'undefined' && root
        ? new IntersectionObserver(entries => {
            const visible = entries.some(entry => entry.isIntersecting);
            if (visible === intersecting) return;
            intersecting = visible;
            if (visible) lastActivity = Date.now();
            void tick();
          })
        : undefined;
    if (root) observer?.observe(root);

    void tick();
    const interval = window.setInterval(
      () => void tick(),
      REVIEW_HEARTBEAT_INTERVAL
    );

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      observer?.disconnect();
      events.forEach(event => document.removeEventListener(event, activity));
      document.removeEventListener('visibilitychange', visibilityChanged);
      // No synthetic time is credited on unmount. A missed stop is bounded by
      // the server's heartbeat gap; the next session establishes a fresh baseline.
      void client
        .mutate({
          mutation: RecordReviewTimeDocument,
          variables: { learnerTrainingUuid: learnerTrainingId, active: false }
        })
        .catch(() => undefined);
    };
  }, [enabled, learnerTrainingId]);
}
